// LiveKit Safety Wrapper Component
// Wraps any LiveKit component with automatic safety checking

import React, { useEffect } from 'react';
import { useLiveKitSafetyChecker } from '../../hooks/useLiveKitSafetyChecker';

interface LiveKitSafetyWrapperProps {
  children: React.ReactNode;
  autoStart?: boolean;
  checkInterval?: number;
  autoFixEnabled?: boolean;
  enableAlerts?: boolean;
  onHealthChange?: (healthScore: number) => void;
  onIssueDetected?: (issue: any) => void;
  onFixApplied?: (fix: string) => void;
  showHealthIndicator?: boolean;
}

export const LiveKitSafetyWrapper: React.FC<LiveKitSafetyWrapperProps> = ({
  children,
  autoStart = true,
  checkInterval = 3000,
  autoFixEnabled = true,
  enableAlerts = true,
  onHealthChange,
  onIssueDetected,
  onFixApplied,
  showHealthIndicator = false
}) => {
  const safetyChecker = useLiveKitSafetyChecker({
    autoStart,
    checkInterval,
    autoFixEnabled,
    enableAlerts,
    onHealthChange,
    onIssueDetected,
    onFixApplied
  });

  // Health indicator component
  const HealthIndicator = () => {
    if (!showHealthIndicator) return null;

    const getHealthColor = (score: number) => {
      if (score >= 80) return '#4CAF50'; // Green
      if (score >= 60) return '#FF9800'; // Orange
      if (score >= 40) return '#FF5722'; // Red
      return '#F44336'; // Dark red
    };

    const getHealthText = (score: number) => {
      if (score >= 80) return 'Excellent';
      if (score >= 60) return 'Good';
      if (score >= 40) return 'Poor';
      return 'Critical';
    };

    return (
      <div
        style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: getHealthColor(safetyChecker.lastHealthScore),
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          zIndex: 9999,
          opacity: safetyChecker.lastHealthScore < 80 ? 1 : 0.7,
          transition: 'opacity 0.3s ease'
        }}
        title={`Health Score: ${safetyChecker.lastHealthScore}/100`}
      >
        {getHealthText(safetyChecker.lastHealthScore)}
        {safetyChecker.consecutiveFailures > 0 && (
          <span style={{ marginLeft: '4px' }}>
            ({safetyChecker.consecutiveFailures})
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      {children}
      <HealthIndicator />
    </>
  );
};

// Higher-order component for easy integration
export const withLiveKitSafety = <P extends object>(
  Component: React.ComponentType<P>,
  safetyOptions: Omit<LiveKitSafetyWrapperProps, 'children'> = {}
) => {
  const WrappedComponent = (props: P) => (
    <LiveKitSafetyWrapper {...safetyOptions}>
      <Component {...props} />
    </LiveKitSafetyWrapper>
  );

  WrappedComponent.displayName = `withLiveKitSafety(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for manual safety checker control
export const useLiveKitSafetyControl = () => {
  const safetyChecker = useLiveKitSafetyChecker({
    autoStart: false, // Don't start automatically
    autoFixEnabled: true,
    enableAlerts: true
  });

  return {
    startMonitoring: safetyChecker.startMonitoring,
    stopMonitoring: safetyChecker.stopMonitoring,
    forceCheck: safetyChecker.forceCheck,
    setAutoFixEnabled: safetyChecker.setAutoFixEnabled,
    isMonitoring: safetyChecker.isMonitoring,
    lastHealthScore: safetyChecker.lastHealthScore,
    consecutiveFailures: safetyChecker.consecutiveFailures,
    shouldAlert: safetyChecker.shouldAlert
  };
}; 